<template>
  <div class="page-wrapper">
    <nav-header v-if="$route.name=='KnowledgeBase'" />
    
    <div class="kb-container">
      <!-- 左侧分类导航 -->
      <div class="category-nav">
        <div 
          class="nav-item"
          v-for="(item, index) in categories"
          :key="index"
          @click="handleCategoryClick(item)"
          :class="{ active: currentCategory === item.value }"
        >
          <div class="icon-wrapper">
            <i :class="item.icon"></i>
          </div>
          <span class="text">{{ item.label }}</span>
        </div>
      </div>

      <!-- 右侧内容区 -->
      <div class="content-area">
        <!-- 搜索和视图切换 -->
        <div class="search-header">
          <div class="left">
            <el-input
              v-model="searchKey"
              placeholder="搜索知识库..."
              class="search-input"
              prefix-icon="el-icon-search"
              @input="handleSearch"
            />
          </div>
          
          <div class="right">
            <div class="view-switch">
              <el-radio-group v-model="viewType" size="small">
                <el-radio-button label="card">
                  <i class="el-icon-menu"></i>
                </el-radio-button>
                <el-radio-button label="list">
                  <i class="el-icon-s-unfold"></i>
                </el-radio-button>
              </el-radio-group>
            </div>
            
            <el-button type="primary" @click="createKnowledgeBase" v-if="userStore.userInfo.id">
              <i class="el-icon-plus"></i> 创建知识库
            </el-button>
          </div>
        </div>

        <!-- 知识库列表 - 卡片视图 -->
        <div v-if="viewType === 'card'" class="kb-list" v-infinite-scroll="loadMore" infinite-scroll-distance="10">
          <el-row :gutter="24">
            <el-col :xs="24" :sm="12" :md="8" :lg="8" v-for="(item, index) in knowledgeBases" :key="index">
              <div class="kb-card">
                <!-- 封面区域 -->
                <div class="kb-cover" :style="{ backgroundImage: `url(${item.coverImage || 'http://lorempixel.com/400/200'})` }" @click="goToDetail(item.id)">
                  <div class="kb-type" :class="getAccessTypeClass(item.accessType)">
                    {{ getAccessTypeText(item.accessType) }}
                  </div>
                </div>
                
                <!-- 内容区域 -->
                <div class="kb-content">
                  <!-- 主要信息区域 -->
                  <div class="kb-main" @click="goToDetail(item.id)">
                    <h3 class="title">{{ item.name }}</h3>
                    <p class="description">{{ item.description }}</p>
                    
                    <!-- 标签区域 -->
                    <div class="tags-wrapper">
                      <div class="tags" v-if="item.tags">
                        <el-tag
                          v-for="tag in item.tags.split(',')"
                          :key="tag"
                          size="small"
                          effect="plain"
                        >
                          {{ tag }}
                        </el-tag>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 底部信息区域 -->
                  <div class="kb-footer">
                    <div class="stats">
                      <span class="stat-item">
                        <i class="el-icon-document"></i>
                        {{ item.documentCount || 0 }}
                      </span>
                      <span class="stat-item">
                        <i class="el-icon-view"></i>
                        {{ item.viewCount || 0 }}
                      </span>
                      <el-button
                        class="favorite-btn"
                        :type="item.favorited ? 'primary' : ''"
                        size="mini"
                        @click.stop="handleFavorite(item)"
                      >
                        <i class="el-icon-star-off"></i>
                        {{ item.favoriteCount || 0 }}
                      </el-button>
                    </div>

                    <!-- 付费信息 -->
                    <div class="price-info" v-if="item.accessType === 2">
                      <span class="price">¥{{ item.price }}</span>
                      <span class="valid-days">{{ item.validDays }}天</span>
                    </div>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 知识库列表 - 列表视图 -->
        <div v-else class="kb-table" v-infinite-scroll="loadMore" infinite-scroll-distance="10">
          <el-table
            v-loading="loading"
            :data="knowledgeBases"
            style="width: 100%"
          >
            <el-table-column width="60">
              <template slot-scope="scope">
                <el-avatar :size="40" :src="scope.row.coverImage">
                  {{ scope.row.name?.charAt(0) }}
                </el-avatar>
              </template>
            </el-table-column>
            
            <el-table-column prop="name" label="知识库名称">
              <template slot-scope="scope">
                <div class="kb-info-cell">
                  <div class="title-row">
                    <span class="title" @click="goToDetail(scope.row.id)">{{ scope.row.name }}</span>
                    <el-tag 
                      size="mini" 
                      :type="getAccessTypeTagType(scope.row.accessType)"
                    >
                      {{ getAccessTypeText(scope.row.accessType) }}
                    </el-tag>
                  </div>
                  <div class="description">{{ scope.row.description }}</div>
                  <div class="tags" v-if="scope.row.tags">
                    <el-tag
                      v-for="tag in scope.row.tags.split(',')"
                      :key="tag"
                      size="mini"
                      effect="plain"
                    >
                      {{ tag }}
                    </el-tag>
                  </div>
                </div>
              </template>
            </el-table-column>


            <el-table-column label="统计" width="200">
              <template slot-scope="scope">
                <div class="stats-cell">
                  <span>
                    <i class="el-icon-document"></i>
                    {{ scope.row.documentCount || 0 }}
                  </span>
                  <span>
                    <i class="el-icon-view"></i>
                    {{ scope.row.viewCount || 0 }}
                  </span>
                  <span>
                    <i class="el-icon-star-off"></i>
                    {{ scope.row.favoriteCount || 0 }}
                  </span>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="价格" width="120" align="right">
              <template slot-scope="scope">
                <template v-if="scope.row.accessType === 2">
                  <div class="price-cell">
                    <span class="price">¥{{ scope.row.price }}</span>
                    <span class="valid-days">{{ scope.row.validDays }}天</span>
                  </div>
                </template>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  :type="scope.row.favorited ? 'primary' : 'default'"
                  size="mini"
                  @click.stop="handleFavorite(scope.row)"
                >
                  <i class="el-icon-star-off"></i>
                  {{ scope.row.favorited ? '已收藏' : '收藏' }}
                  <span v-if="scope.row.favoriteCount">({{ scope.row.favoriteCount }})</span>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 加载状态 -->
        <div class="loading-status" v-if="loading || finished">
          <p v-if="loading" class="loading">
            <i class="el-icon-loading"></i> 加载中...
          </p>
          <p v-if="finished" class="no-more">没有更多了</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavHeader from '@/components/layout/NavHeader.vue'
import { listKnowledgeBase } from '@/api/knowledgeBase/knowledgeBase'
import { toggleFavorite } from '@/api/knowledgeBase/favorite'
import _ from 'lodash'
import { getDictList } from '@/api/system/dict'

export default {
  name: 'KnowledgeBase',
  
  components: {
    NavHeader
  },

  data() {
    return {
      knowledgeBases: [],
      page: 1,
      pageSize: 9,
      loading: false,
      finished: false,
      searchKey: '',
      viewType: 'card',
      categories: [],
      currentCategory: null,
    }
  },

  created() {
    this.getCategories()
    this.getKnowledgeBaseList()
  },

  methods: {
    // 获取知识库列表
    async getKnowledgeBaseList() {
      try {
        this.loading = true
        const res = await listKnowledgeBase({
          pageNum: this.page,
          pageSize: this.pageSize,
          name: this.searchKey,
          categoryId: this.currentCategory
        })

        this.knowledgeBases = [...this.knowledgeBases, ...res.rows]
        
        if (res.total <= this.knowledgeBases.length) {
          this.finished = true
        }
      } catch (error) {
        console.error('获取知识库列表失败:', error)
        this.finished = true
      } finally {
        this.loading = false
      }
    },

    // 加载更多
    loadMore() {
      if (this.loading || this.finished) return
      this.page++
      this.getKnowledgeBaseList()
    },

    // 搜索处理
    handleSearch: _.debounce(function() {
      this.knowledgeBases = []
      this.page = 1
      this.finished = false
      this.getKnowledgeBaseList()
    }, 300),

    // 跳转到详情页
    goToDetail(id) {
      this.$router.push(`/knowledgeBase/detail/${id}`)
    },

    // 创建知识库
    createKnowledgeBase() {
      this.$router.push('/knowledgeBase/create')
    },

    // 获取访问类型样式
    getAccessTypeClass(type) {
      const map = {
        0: 'public',
        1: 'private',
        2: 'paid'
      }
      return map[type] || 'public'
    },

    // 获取访问类型文本
    getAccessTypeText(type) {
      const map = {
        0: '公开',
        1: '私有',
        2: '付费'
      }
      return map[type] || '公开'
    },

    // 获取访问类型对应的Tag类型
    getAccessTypeTagType(type) {
      const map = {
        0: 'success',
        1: 'info',
        2: 'warning'
      }
      return map[type] || 'success'
    },

    // 处理收藏/取消收藏
    async handleFavorite(item) {
      try {
        await toggleFavorite(item.id)
        item.favorited = !item.favorited
        item.favoriteCount = item.favorited ? (item.favoriteCount || 0) + 1 : (item.favoriteCount || 1) - 1
        this.$message.success(item.favorited ? '收藏成功' : '已取消收藏')
      } catch (error) {
        console.error(error)
        this.$message.error('操作失败')
      }
    },

    // 获取分类列表
    async getCategories() {
      try {
        const res = await getDictList('kb_category')
        this.categories = res.rows.map(item => ({
          label: item.dictLabel,
          value: item.dictValue,
          icon: item.cssClass || 'el-icon-folder'
        }))
      } catch (error) {
        console.error('获取分类失败:', error)
      }
    },

    // 点击分类
    handleCategoryClick(category) {
      // 如果点击当前选中的分类,则取消选中
      if (this.currentCategory === category.value) {
        this.currentCategory = null
      } else {
        this.currentCategory = category.value
      }
      
      // 重置分页和列表数据
      this.page = 1
      this.knowledgeBases = []
      this.getKnowledgeBaseList()
    },
  }
}
</script>

<style lang="scss" scoped>
.page-wrapper {
  min-height: 100vh;
  background: #f6f8fa;
}

.kb-container {
  display: flex;
  min-height: 100vh;
  background: #f6f8fa;
  padding: 24px;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

.category-nav {
  width: 200px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  height: fit-content;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  .nav-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 4px;

    &:hover {
      background: #f5f7fa;
    }

    &.active {
      color: #ff6b00;
      background: rgba(255, 107, 0, 0.1);
    }

    .icon-wrapper {
      margin-right: 12px;
      font-size: 18px;
    }

    .text {
      font-size: 14px;
    }
  }
}

.content-area {
  flex: 1;
  min-width: 0;
}

.search-header {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  
  .left {
    width: 300px;
  }
  
  .right {
    display: flex;
    gap: 16px;
    align-items: center;
  }
}

.view-switch {
  display: flex;
  gap: 8px;
}

.kb-list {
  .el-row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;
  }
  
  .el-col {
    padding: 0 12px;
    margin-bottom: 24px;
  }

  .kb-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    border: 1px solid #eee;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .kb-cover {
      height: 180px;
      flex-shrink: 0;
      background-size: cover;
      background-position: center;
      position: relative;
      
      .kb-type {
        position: absolute;
        top: 12px;
        right: 12px;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        
        &.public {
          background: rgba(52, 211, 153, 0.1);
          color: #34D399;
        }
        
        &.private {
          background: rgba(99, 102, 241, 0.1);
          color: #6366F1;
        }
        
        &.paid {
          background: rgba(245, 158, 11, 0.1);
          color: #F59E0B;
        }
      }
    }
    
    .kb-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 16px;
    }
    
    .kb-main {
      flex: 1;
      
      .title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin-bottom: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .description {
        color: #666;
        font-size: 14px;
        margin-bottom: 16px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 40px;
      }
      
      .tags-wrapper {
        min-height: 32px;
        margin-bottom: 16px;
        
        .tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          
          .el-tag {
            margin: 0;
          }
        }
      }
    }
    
    .kb-footer {
      margin-top: auto;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
      
      .author-info {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        
        .author-name {
          font-size: 14px;
          color: #666;
        }
      }
      
      .stats {
        display: flex;
        align-items: center;
        gap: 16px;
        
        .stat-item {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #666;
          font-size: 13px;
        }
        
        .favorite-btn {
          padding: 4px 8px;
          height: 24px;
          line-height: 1;
          border-radius: 12px;
          
          &.el-button--primary {
            background-color: #409EFF;
            border-color: #409EFF;
            color: #fff;
            
            &:hover {
              background-color: #66b1ff;
              border-color: #66b1ff;
            }
          }
          
          &.el-button--default {
            background-color: #fff;
            border-color: #dcdfe6;
            color: #666;
            
            &:hover {
              color: #409EFF;
              border-color: #409EFF;
            }
          }
          
          i {
            margin-right: 4px;
            font-size: 14px;
          }
        }
      }
      
      .price-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 12px;
        border-top: 1px solid #f0f0f0;
        
        .price {
          color: #ff4d4f;
          font-size: 18px;
          font-weight: 500;
        }
        
        .valid-days {
          color: #999;
          font-size: 13px;
        }
      }
    }
  }
}

.kb-table {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;
  
  .kb-info-cell {
    .title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
      
      .title {
        color: #409EFF;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    
    .description {
      color: #666;
      font-size: 13px;
      margin-bottom: 8px;
    }
    
    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
  }
  
  .author-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    
    span {
      color: #666;
      font-size: 13px;
    }
  }
  
  .stats-cell {
    display: flex;
    gap: 16px;
    color: #666;
    font-size: 13px;
    
    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  
  .price-cell {
    .price {
      color: #ff4d4f;
      font-weight: 500;
      margin-right: 4px;
    }
    
    .valid-days {
      color: #999;
      font-size: 12px;
    }
  }
}

.loading-status {
  text-align: center;
  padding: 20px 0;
  color: #999;
  font-size: 14px;
  
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

// 响应式布局
@media screen and (max-width: 768px) {
  .kb-container {
    flex-direction: column;
    padding: 16px;
  }

  .category-nav {
    width: 100%;
    margin-bottom: 16px;
    padding: 12px;
    
    .nav-item {
      padding: 8px 12px;
    }
  }
  
  .search-header {
    flex-direction: column;
    
    .left {
      width: 100%;
    }
    
    .right {
      width: 100%;
      justify-content: space-between;
    }
  }
  
  .kb-list {
    .el-row {
      margin: 0 -8px;
    }
    
    .el-col {
      padding: 0 8px;
      margin-bottom: 16px;
    }
  }
  
  .kb-table {
    :deep(.el-table) {
      /* 移动端表格样式调整 */
      .el-table__header {
        display: none;
      }
      
      .el-table__body {
        tr {
          display: flex;
          flex-direction: column;
          padding: 12px;
          border-bottom: 1px solid #eee;
          
          td {
            display: block;
            border: none;
            padding: 4px 0;
          }
        }
      }
    }
  }
}
</style> 